import { SkyText } from "@skypointcloud/skypoint-ui";
import type { OverviewImports } from "@shared/types/overview";
import { TableObjectIcon } from "@/lib/icons";
import { Fact, OverviewCard, statusTone } from "./OverviewCard";
import { formatRowCount, hasImportTotals, importFootnotes } from "./overviewFormatting";

/**
 * Rows imported, last 24 hours (contract §3, card 5; decision Q3). The label carries the window
 * because the window is rolling: a calendar day needs a zone and the instance has none.
 *
 * A sum nobody reported is a dash, and a window with no tables at all says so in a sentence,
 * never as `0` (P1: `rowsExtracted` and `rowsLoaded` are null then).
 */
export function ImportsCard({ imports }: { imports: OverviewImports }) {
  const notes = importFootnotes(imports);

  return (
    <OverviewCard
      id="imports"
      title="Rows imported, last 24 hours"
      icon={TableObjectIcon}
      tone={statusTone({
        failed: imports.tablesFailed,
        warning: 0,
        ok: hasImportTotals(imports) ? imports.tables : 0,
      })}
      testId="overview-imports"
    >
      {hasImportTotals(imports) ? (
        <dl className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-3 sm:grid-cols-3">
          <Fact term="Loaded" testId="overview-imports-loaded">
            {formatRowCount(imports.rowsLoaded)}
          </Fact>
          <Fact term="Extracted" testId="overview-imports-extracted">
            {formatRowCount(imports.rowsExtracted)}
          </Fact>
          <Fact term="Tables" testId="overview-imports-tables">
            {imports.tables.toLocaleString()}
            {imports.tablesFailed > 0 ? (
              <span className="text-destructive">{` · ${imports.tablesFailed.toLocaleString()} failed`}</span>
            ) : null}
          </Fact>
        </dl>
      ) : (
        <SkyText size={300} color="muted" block testId="overview-imports-none">
          No imports in the last 24 hours.
        </SkyText>
      )}

      {notes.length > 0 ? (
        <ul className="flex flex-col gap-1" data-testid="overview-imports-notes">
          {notes.map((note) => (
            <li key={note}>
              <SkyText size={200} color="muted" block>
                {note}
              </SkyText>
            </li>
          ))}
        </ul>
      ) : null}
    </OverviewCard>
  );
}
